import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// Async thunk for creating a session
export const createSession = createAsyncThunk(
  'sessions/createSession',
  async ({ token, profileId, draftId, startFresh = false }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/sessions?draftId=${draftId}${startFresh ? '&startFresh=true' : ''}`,
        {
          method: 'POST',
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || 'Failed to create session')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

// Async thunk for fetching a session by ID
export const fetchSession = createAsyncThunk(
  'sessions/fetchSession',
  async ({ token, profileId, sessionId }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/sessions/${sessionId}`,
        { method: 'GET' },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || 'Failed to fetch session')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

// Async thunk for starting a session (transitions from READY to IN_PROGRESS)
// Uses PUT /v2/sessions/{id} to update the session status
export const startSession = createAsyncThunk(
  'sessions/startSession',
  async ({ token, profileId, sessionId, sessionData }, { rejectWithValue }) => {
    try {
      // Update session with IN_PROGRESS status
      const updatedSession = {
        ...sessionData,
        status: 'IN_PROGRESS',
        startedAt: new Date().toISOString(),
      }

      const response = await apiCallWithHost(
        `/v2/sessions/${sessionId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedSession),
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || 'Failed to start session')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

// Async thunk for saving session progress (PUT to persist iteration updates)
export const saveSessionProgress = createAsyncThunk(
  'sessions/saveSessionProgress',
  async ({ token, profileId, sessionId, sessionData }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/sessions/${sessionId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(sessionData),
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || 'Failed to save session progress')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

// Async thunk for completing a session
export const completeSession = createAsyncThunk(
  'sessions/completeSession',
  async ({ token, profileId, sessionId, sessionData }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/sessions/${sessionId}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(sessionData),
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || 'Failed to complete session')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

// Async thunk for fetching current active session
export const fetchCurrentSession = createAsyncThunk(
  'sessions/fetchCurrentSession',
  async ({ token, profileId }, { rejectWithValue }) => {
    try {
      // This would need a new API endpoint - for now we'll handle it in the component
      // TODO: Add GET /v2/sessions/current endpoint
      return null
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

// Async thunk for hiding a workout from available workouts
export const hideWorkout = createAsyncThunk(
  'sessions/hideWorkout',
  async ({ token, profileId, draftId }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/sessions/available-workouts/${draftId}/hide`,
        { method: 'POST' },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || 'Failed to hide workout')
      }

      return { draftId }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

// Async thunk for unhiding a workout (making it visible in available workouts)
export const unhideWorkout = createAsyncThunk(
  'sessions/unhideWorkout',
  async ({ token, profileId, draftId }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/sessions/available-workouts/${draftId}/unhide`,
        { method: 'POST' },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || 'Failed to unhide workout')
      }

      return { draftId }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

const initialState = {
  currentSession: null,
  sessionHistory: [],
  status: 'idle',
  error: null,
  loading: false,
}

const sessionsSlice = createSlice({
  name: 'sessions',
  initialState,
  reducers: {
    clearCurrentSession: (state) => {
      state.currentSession = null
    },
    updateSessionIteration: (state, action) => {
      const { iterationId, updates } = action.payload
      console.debug('Redux: updateSessionIteration called', { iterationId, updates })
      if (!state.currentSession) {
        console.debug('Redux: No current session')
        return
      }

      let found = false

      // Helper function to update iteration in a group
      const updateIterationInGroup = (group) => {
        if (group.iterations) {
          group.iterations.forEach((iteration) => {
            if (iteration.id === iterationId) {
              console.debug('Redux: Found iteration, updating', {
                before: { reps: iteration.reps, status: iteration.status },
                after: updates,
              })
              Object.assign(iteration, updates)
              found = true
            }
          })
        }
      }

      // Handle direct exerciseGroups structure
      if (state.currentSession.exerciseGroups) {
        state.currentSession.exerciseGroups.forEach(updateIterationInGroup)
      }

      // Handle ruleSets structure (nested: ruleSets -> iterationSet -> exerciseGroups)
      if (state.currentSession.ruleSets) {
        state.currentSession.ruleSets.forEach((ruleSet) => {
          if (ruleSet.iterationSet?.exerciseGroups) {
            ruleSet.iterationSet.exerciseGroups.forEach(updateIterationInGroup)
          }
        })
      }

      if (!found) {
        console.warn('Redux: Iteration not found for update', iterationId)
      } else {
        console.debug('Redux: Update successful, new state:', state.currentSession)
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Create session
      .addCase(createSession.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.loading = false
        state.currentSession = action.payload
        state.status = 'idle'
      })
      .addCase(createSession.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Fetch session
      .addCase(fetchSession.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSession.fulfilled, (state, action) => {
        state.loading = false
        state.currentSession = action.payload
        state.status = 'idle'
      })
      .addCase(fetchSession.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Start session - only update status, don't overwrite entire session
      // to preserve any local iteration updates made during the request
      .addCase(startSession.pending, (state) => {
        state.error = null
      })
      .addCase(startSession.fulfilled, (state, action) => {
        if (state.currentSession) {
          // Only update status and timestamps, preserve local iteration changes
          state.currentSession.status = action.payload.status || 'IN_PROGRESS'
          state.currentSession.startedAt = action.payload.startedAt
        } else {
          state.currentSession = action.payload
        }
        state.status = 'idle'
      })
      .addCase(startSession.rejected, (state, action) => {
        state.error = action.payload
      })
      // Save session progress (don't set loading to avoid UI flicker)
      // Don't overwrite local session to preserve any updates made during the request
      .addCase(saveSessionProgress.pending, (state) => {
        state.error = null
      })
      .addCase(saveSessionProgress.fulfilled, (state, action) => {
        // Update status to IN_PROGRESS if it was READY (session has started)
        if (state.currentSession && state.currentSession.status === 'READY') {
          state.currentSession.status = 'IN_PROGRESS'
          state.currentSession.startedAt =
            state.currentSession.startedAt || new Date().toISOString()
        }
        console.debug('Session progress saved to server')
      })
      .addCase(saveSessionProgress.rejected, (state, action) => {
        state.error = action.payload
        console.error('Failed to save session progress:', action.payload)
      })
      // Complete session
      .addCase(completeSession.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(completeSession.fulfilled, (state, action) => {
        state.loading = false
        // Move current session to history
        if (state.currentSession) {
          state.sessionHistory.unshift(state.currentSession)
        }
        state.currentSession = null
        state.status = 'idle'
      })
      .addCase(completeSession.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearCurrentSession, updateSessionIteration } = sessionsSlice.actions

export const selectCurrentSession = (state) => state.sessions.currentSession
export const selectSessionHistory = (state) => state.sessions.sessionHistory
export const selectSessionsStatus = (state) => state.sessions.status
export const selectSessionsError = (state) => state.sessions.error
export const selectSessionsLoading = (state) => state.sessions.loading

export default sessionsSlice.reducer
